import { computed, ComputedRef } from "vue";
import { useThemeStore } from "@/stores/theme";

interface ThemeComposableReturn {
  // 설정된 모드 (사용자 선택)
  isDarkTheme: ComputedRef<boolean>;
  isLightTheme: ComputedRef<boolean>;
  isSystemTheme: ComputedRef<boolean>;
  isMonochrome: ComputedRef<boolean>;

  // 실제 적용된 테마 (시각적 표현)
  isDark: ComputedRef<boolean>;
  isLight: ComputedRef<boolean>;

  // 액션
  setTheme: (mode: "system" | "dark" | "light") => void;
  getTheme: () => string;
  toggleMonochrome: () => void;
  enableMonochrome: () => void;
  disableMonochrome: () => void;

  // 현재 활성화된 테마
  activeTheme: ComputedRef<"dark" | "light">;
}

export default function useTheme(): ThemeComposableReturn {
  const themeStore = useThemeStore();

  // 테마 모드 설정 확인 (사용자가 선택한 모드)
  const isDarkTheme = computed(() => themeStore.isDarkMode);
  const isLightTheme = computed(() => themeStore.isLightMode);
  const isSystemTheme = computed(() => themeStore.isSystemMode);

  // 실제 적용된 테마 확인 (시각적으로 보이는 모드)
  const isDark = computed(() => themeStore.isDark);
  const isLight = computed(() => themeStore.isLight);

  // 모노크롬 모드 상태 확인
  const isMonochrome = computed(() => themeStore.isMonochrome);

  // 색상 모드 설정 및 조회
  const setTheme = (value: "system" | "dark" | "light"): void => themeStore.setColorMode(value);
  const getTheme = (): string => themeStore.colorMode;

  // 모노크롬 모드 제어
  const toggleMonochrome = (): void => themeStore.toggleDisplayMode();

  const enableMonochrome = (): void => {
    if (!themeStore.isMonochrome) {
      themeStore.setDisplayMode("monochrome");
    }
  };

  const disableMonochrome = (): void => {
    if (themeStore.isMonochrome) {
      themeStore.setDisplayMode("color");
    }
  };

  // 현재 적용된 외관 모드
  const activeTheme = computed<"dark" | "light">(() => themeStore.activeTheme);

  return {
    // 사용자 설정
    isDarkTheme,
    isLightTheme,
    isSystemTheme,
    isMonochrome,

    // 실제 적용된 테마
    isDark,
    isLight,

    // 기능
    setTheme,
    getTheme,
    toggleMonochrome,
    enableMonochrome,
    disableMonochrome,
    activeTheme,
  };
}
